import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  useTheme,
  TextField,
  Paper,
  Chip,
  alpha,
  AppBar,
  Toolbar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaymentIcon from '@mui/icons-material/Payment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportIcon from '@mui/icons-material/Support';
import SendIcon from '@mui/icons-material/Send';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InstagramIcon from '@mui/icons-material/Instagram';

const features = [
  {
    icon: <PeopleIcon sx={{ fontSize: 48 }} />,
    title: 'Student Management',
    description: 'Complete student lifecycle management with enrollment, records, and comprehensive profiles.',
  },
  {
    icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
    title: 'Academic Excellence',
    description: 'Exams, results, marks, and grade management with detailed analytics and performance tracking.',
  },
  {
    icon: <PaymentIcon sx={{ fontSize: 48 }} />,
    title: 'Fee Management',
    description: 'Automated fee collection, invoice generation, payment tracking, and financial reporting.',
  },
  {
    icon: <AnalyticsIcon sx={{ fontSize: 48 }} />,
    title: 'Analytics & Insights',
    description: 'Real-time dashboards, performance metrics, and comprehensive reporting tools.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security with role-based access control and data encryption.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 48 }} />,
    title: 'Fast & Efficient',
    description: 'Lightning-fast performance with cloud-based infrastructure and 99.9% uptime guarantee.',
  },
];

const pricingPlans = [
  {
    name: 'Basic',
    originalPrice: '₹2,999',
    price: '₹2,499',
    period: '/month',
    studentRange: '0-500 Students',
    features: [
      'Complete student & parent management',
      'Attendance tracking & reporting',
      'Fee management & payments',
      'Exam & results management',
      'Class & subject management',
      'Parent portal access',
      'Mobile app for parents',
      'Real-time analytics & insights',
      'Email/WhatsApp support',
      'Standard reports',
    ],
    popular: false,
  },
  {
    name: 'Advance',
    originalPrice: '₹4,999',
    price: '₹3,999',
    period: '/month',
    studentRange: '500-1500 Students',
    features: [
      'Complete student & parent management',
      'Attendance tracking & reporting',
      'Fee management & payments',
      'Exam & results management',
      'Class & subject management',
      'Parent portal access',
      'Mobile app for parents',
      'Real-time analytics & insights',
      'Priority support',
      'Custom reports',
    ],
    popular: true,
  },
  {
    name: 'Pro',
    originalPrice: '₹6,999',
    price: '₹5,999',
    period: '/month',
    studentRange: '1500+ Students',
    features: [
      'Complete student & parent management',
      'Attendance tracking & reporting',
      'Fee management & payments',
      'Exam & results management',
      'Class & subject management',
      'Parent portal access',
      'Mobile app for parents',
      'Real-time analytics & insights',
      'Dedicated support',
      'Custom integrations',
    ],
    popular: false,
  },
];

const ProductLandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const API_BASE_URL = 'https://api.ptshub.in';
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState({ type: '', text: '' });
  const [demoMessage, setDemoMessage] = useState({ type: '', text: '' });
  const [contactPhoneError, setContactPhoneError] = useState('');
  const [demoPhoneError, setDemoPhoneError] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [demoEmailError, setDemoEmailError] = useState('');

  const handleScrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const validatePhone = (phone, setError) => {
    if (!phone) {
      setError('Phone number is required');
      return false;
    }
    
    // Remove any spaces or dashes for validation
    const cleanedPhone = phone.replace(/[\s-]/g, '');
    
    // Check if it's exactly 10 digits
    if (!/^\d{10}$/.test(cleanedPhone)) {
      setError('Phone number must be exactly 10 digits');
      return false;
    }
    
    // Check if it starts with 6, 7, 8, or 9
    if (!/^[6789]/.test(cleanedPhone)) {
      setError('Phone number must start with 6, 7, 8, or 9');
      return false;
    }
    
    setError('');
    return true;
  };

  const validateEmail = (email, setError) => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    setError('');
    return true;
  };

  const handleContactPhoneChange = (e) => {
    const value = e.target.value;
    
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digitsOnly.slice(0, 10);
    
    setContactForm({ ...contactForm, phone: limitedDigits });
    
    // Validate
    validatePhone(limitedDigits, setContactPhoneError);
  };

  const handleDemoPhoneChange = (e) => {
    const value = e.target.value;
    
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digitsOnly.slice(0, 10);
    
    setDemoForm({ ...demoForm, phone: limitedDigits });
    
    // Validate
    validatePhone(limitedDigits, setDemoPhoneError);
  };

  const handleContactEmailChange = (e) => {
    const value = e.target.value;
    setContactForm({ ...contactForm, email: value });
    validateEmail(value, setContactEmailError);
  };

  const handleDemoEmailChange = (e) => {
    const value = e.target.value;
    setDemoForm({ ...demoForm, email: value });
    validateEmail(value, setDemoEmailError);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const isPhoneValid = validatePhone(contactForm.phone, setContactPhoneError);
    const isEmailValid = validateEmail(contactForm.email, setContactEmailError);
    
    if (!contactForm.name.trim()) {
      setContactMessage({ type: 'error', text: 'Name is required' });
      return;
    }
    
    if (!contactForm.message.trim()) {
      setContactMessage({ type: 'error', text: 'Message is required' });
      return;
    }
    
    if (!isPhoneValid || !isEmailValid) {
      return;
    }
    
    setContactLoading(true);
    setContactMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/contact/index.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone || null,
          subject: 'Contact Request',
          message: contactForm.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setContactMessage({ type: 'success', text: data.message || 'Thank you for contacting us! We will get back to you soon.' });
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setContactPhoneError('');
        setContactEmailError('');
      } else {
        setContactMessage({ type: 'error', text: data.error || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setContactMessage({ type: 'error', text: 'Failed to send message. Please try again later.' });
    } finally {
      setContactLoading(false);
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const isPhoneValid = validatePhone(demoForm.phone, setDemoPhoneError);
    const isEmailValid = validateEmail(demoForm.email, setDemoEmailError);
    
    if (!demoForm.name.trim()) {
      setDemoMessage({ type: 'error', text: 'Name is required' });
      return;
    }
    
    if (!demoForm.school.trim()) {
      setDemoMessage({ type: 'error', text: 'School name is required' });
      return;
    }
    
    if (!isPhoneValid || !isEmailValid) {
      return;
    }
    
    setDemoLoading(true);
    setDemoMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/contact/index.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: demoForm.name,
          email: demoForm.email,
          phone: demoForm.phone || null,
          subject: 'Demo Request',
          message: `School: ${demoForm.school}\n\nDemo Request from landing page.`,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setDemoMessage({ type: 'success', text: data.message || 'Thank you for your interest! We will contact you soon to schedule a demo.' });
        setDemoForm({ name: '', email: '', phone: '', school: '' });
        setDemoPhoneError('');
        setDemoEmailError('');
      } else {
        setDemoMessage({ type: 'error', text: data.error || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setDemoMessage({ type: 'error', text: 'Failed to submit request. Please try again later.' });
    } finally {
      setDemoLoading(false);
    }
  };

  const handleInputChange = (field, formType) => (e) => {
    if (formType === 'contact') {
      setContactForm({ ...contactForm, [field]: e.target.value });
    } else {
      setDemoForm({ ...demoForm, [field]: e.target.value });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Header Navigation */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.common.white,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          py: 1,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src="/PTSHubLogo.png"
                alt="PTS Hub Logo"
                sx={{
                  width: { xs: 120, sm: 150, md: 200 },
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Stack>
            {isMobile ? (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: theme.palette.grey[900] }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  onClick={() => {
                    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    color: theme.palette.grey[700],
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Features
                </Button>
                <Button
                  onClick={() => {
                    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    color: theme.palette.grey[700],
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Pricing
                </Button>
                <Button
                  onClick={() => {
                    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    color: theme.palette.grey[700],
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Contact
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleScrollToContact();
                    setMobileMenuOpen(false);
                  }}
                  startIcon={<CalendarTodayIcon />}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.black,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: { sm: 2, md: 2.5 },
                    py: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Book Now
                </Button>
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '80%', sm: 300 },
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stack spacing={2}>
          <Button
            fullWidth
            onClick={() => {
              document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
              setMobileMenuOpen(false);
            }}
            sx={{
              color: theme.palette.grey[700],
              fontWeight: 600,
              textTransform: 'none',
              justifyContent: 'flex-start',
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: 'transparent',
              },
            }}
          >
            Features
          </Button>
          <Button
            fullWidth
            onClick={() => {
              document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
              setMobileMenuOpen(false);
            }}
            sx={{
              color: theme.palette.grey[700],
              fontWeight: 600,
              textTransform: 'none',
              justifyContent: 'flex-start',
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: 'transparent',
              },
            }}
          >
            Pricing
          </Button>
          <Button
            fullWidth
            onClick={() => {
              document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
              setMobileMenuOpen(false);
            }}
            sx={{
              color: theme.palette.grey[700],
              fontWeight: 600,
              textTransform: 'none',
              justifyContent: 'flex-start',
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: 'transparent',
              },
            }}
          >
            Contact
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleScrollToContact();
              setMobileMenuOpen(false);
            }}
            startIcon={<CalendarTodayIcon />}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.common.black,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              mt: 2,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Book Now
          </Button>
        </Stack>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: '85vh', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.common.white, 1)} 100%)`,
          pt: { xs: 4, md: 8 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                <Chip
                  label="School Management System"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 32,
                    borderRadius: '20px',
                    width: 'fit-content',
                  }}
                />
                
                <Typography
                  variant="h1"
                  sx={{
                    color: theme.palette.grey[900],
                    fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem', lg: '4.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Transform Your School Management with{' '}
                  <Box component="span" sx={{ color: theme.palette.primary.main }}>
                    PTS Hub
                  </Box>
                </Typography>
                
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.grey[600],
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' },
                    fontWeight: 400,
                    lineHeight: 1.7,
                    maxWidth: { xs: '100%', md: '90%' },
                  }}
                >
                  Streamline admissions, academics, finance, and communication in one powerful platform. 
                  Empower your team to work smarter and delight families with modern, mobile-first experiences.
                </Typography>
                
                {/* CTA Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    endIcon={<PlayArrowIcon />}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.common.black,
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Book a Demo
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleScrollToContact}
                    sx={{
                      borderColor: theme.palette.grey[300],
                      borderWidth: 2,
                      color: theme.palette.grey[900],
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Get Started
                  </Button>
                </Stack>

                {/* Trust Indicators */}
                <Stack 
                  direction="row" 
                  spacing={{ xs: 3, sm: 4 }} 
                  sx={{ 
                    mt: { xs: 3, md: 4 }, 
                    flexWrap: 'wrap', 
                    gap: { xs: 2, sm: 2 },
                    justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                  }}
                >
                  <Box>
                    <Typography 
                      variant="h4" 
                      fontWeight={800} 
                      sx={{ 
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      }}
                    >
                      2+
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.grey[600], 
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Schools
                    </Typography>
                  </Box>
                  <Box>
                    <Typography 
                      variant="h4" 
                      fontWeight={800} 
                      sx={{ 
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      }}
                    >
                      500+
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.grey[600], 
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Students
                    </Typography>
                  </Box>
                  <Box>
                    <Typography 
                      variant="h4" 
                      fontWeight={800} 
                      sx={{ 
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      }}
                    >
                      25+
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.grey[600], 
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Teachers
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            
            {/* Right Column - Product Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/banner1.png"
                  alt="PTS Hub School Management System"
                  sx={{
                    width: '100%',
                    maxWidth: 600,
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Product Video Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: theme.palette.common.white,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Chip
                  label="See It In Action"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 32,
                    borderRadius: '20px',
                    width: 'fit-content',
                  }}
                />
                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    color: theme.palette.grey[900],
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Watch PTS Hub Transform Your School
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.grey[600],
                    fontWeight: 400,
                    lineHeight: 1.7,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  }}
                >
                  See how PTS Hub simplifies school management and empowers your team. 
                  Experience the power of streamlined operations, real-time insights, and 
                  seamless communication—all in one comprehensive platform.
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {[
                    'Complete school management in one platform',
                    'Real-time attendance and academic tracking',
                    'Automated fee management and payments',
                    'Seamless parent-teacher communication',
                  ].map((feature, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: theme.palette.grey[700], fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>

            {/* Right Column - Video */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '300px', md: '350px' },
                    margin: '0 auto',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
                    position: 'relative',
                    bgcolor: theme.palette.grey[900],
                    aspectRatio: '9/16',
                  }}
                >
                  <Box
                    component="video"
                    src="/1765111603920692.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 4,
                    }}
                  >
                    Your browser does not support the video tag.
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mobile App Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Column - Mobile App Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'transparent',
                }}
              >
                <Box
                  component="img"
                  src="/mobile_app1.png"
                  alt="Mobile App - Parent Portal"
                  sx={{
                    width: '100%',
                    maxWidth: { xs: 400, sm: 500, md: 600 },
                    height: 'auto',
                    bgcolor: 'transparent',
                    animation: 'floatMobile 6s ease-in-out infinite',
                    transition: 'all 0.3s ease',
                    '@keyframes floatMobile': {
                      '0%, 100%': {
                        transform: 'translateY(0px)',
                      },
                      '50%': {
                        transform: 'translateY(-20px)',
                      },
                    },
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.05)',
                      animation: 'none',
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Right Column - Content */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Chip
                  label="Mobile Application"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 32,
                    borderRadius: '20px',
                    width: 'fit-content',
                  }}
                />
                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    color: theme.palette.grey[900],
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '2.75rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Dedicated Mobile App for Parents
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.grey[600],
                    fontWeight: 400,
                    lineHeight: 1.7,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  }}
                >
                  Our comprehensive mobile application empowers parents to track their child's activities, 
                  monitor academic progress, and receive real-time updates—all from their smartphone.
                </Typography>
                
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {[
                    'Real-time attendance tracking',
                    'Academic performance reports',
                    'Teacher feedback & communication',
                    'Fee management & payments',
                  ].map((feature, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: theme.palette.grey[700], fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Admin & Teacher Portal Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: theme.palette.common.white,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Chip
                  label="Web Portal"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 32,
                    borderRadius: '20px',
                    width: 'fit-content',
                  }}
                />
                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    color: theme.palette.grey[900],
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '2.75rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Dedicated Portal for School Admin & Teachers
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.grey[600],
                    fontWeight: 400,
                    lineHeight: 1.7,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  }}
                >
                  Our comprehensive web-based portal provides school administrators and teachers with powerful tools 
                  to manage students, track attendance, handle academics, and streamline all school operations efficiently.
                </Typography>
                
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {[
                    'Complete student and parent management',
                    'Attendance tracking and reporting',
                    'Exam and results management',
                    'Fee collection and financial tracking',
                    'Class and subject management',
                    'Real-time analytics and insights',
                  ].map((feature, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: theme.palette.grey[700], fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Grid>

            {/* Right Column - Desktop Portal Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'transparent',
                }}
              >
                <Box
                  component="img"
                  src="/PTSHubDesktop.png"
                  alt="PTS Hub Desktop Portal - Admin & Teacher Dashboard"
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 600, md: 700 },
                    height: 'auto',
                    bgcolor: 'transparent',
                    animation: 'floatDesktop 8s ease-in-out infinite',
                    transition: 'all 0.3s ease',
                    '@keyframes floatDesktop': {
                      '0%, 100%': {
                        transform: 'translateY(0px)',
                      },
                      '50%': {
                        transform: 'translateY(-25px)',
                      },
                    },
                    '&:hover': {
                      transform: 'translateY(-15px) scale(1.03)',
                      animation: 'none',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        id="features-section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: theme.palette.common.white,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" sx={{ mb: 8 }}>
            <Chip
              label="Features"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 32,
                borderRadius: '20px',
              }}
            />
            <Typography
              variant="h2"
              fontWeight={800}
              textAlign="center"
              sx={{
                color: theme.palette.grey[900],
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                letterSpacing: '-0.02em',
                px: { xs: 2, sm: 0 },
              }}
            >
              Everything You Need to Manage Your School
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{
                color: theme.palette.grey[600],
                maxWidth: { xs: '100%', sm: '700px' },
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Powerful features designed to streamline operations and enhance the educational experience
            </Typography>
          </Stack>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: theme.palette.grey[900] }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.grey[600], lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        id="pricing-section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" sx={{ mb: 8 }}>
            <Chip
              label="Pricing"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 32,
                borderRadius: '20px',
              }}
            />
            <Typography
              variant="h2"
              fontWeight={800}
              textAlign="center"
              sx={{
                color: theme.palette.grey[900],
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                letterSpacing: '-0.02em',
                px: { xs: 2, sm: 0 },
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{
                color: theme.palette.grey[600],
                maxWidth: { xs: '100%', sm: '700px' },
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Choose the perfect plan for your school. All plans include 14-day free trial.
            </Typography>
          </Stack>
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    position: 'relative',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: theme.palette.common.white,
                    border: plan.popular
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                    },
                  }}
                >
                  {plan.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <Chip
                        label="Most Popular"
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.common.black,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: 32,
                          borderRadius: '20px',
                        }}
                      />
                    </Box>
                  )}
                  <Stack spacing={3} sx={{ pt: plan.popular ? 1 : 0 }}>
                    <Box>
                      <Typography variant="h4" fontWeight={900} sx={{ mb: 0.5, color: theme.palette.grey[900] }}>
                        {plan.name}
                      </Typography>
                      {plan.studentRange && (
                        <Typography variant="body1" sx={{ color: theme.palette.primary.main, fontWeight: 700, mt: 0.5 }}>
                          {plan.studentRange}
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={0.5}>
                      {plan.originalPrice && (
                        <Typography
                          variant="h6"
                          sx={{
                            color: theme.palette.grey[500],
                            fontSize: '1.25rem',
                            textDecoration: 'line-through',
                            fontWeight: 500,
                          }}
                        >
                          {plan.originalPrice}
                        </Typography>
                      )}
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography variant="h2" fontWeight={900} sx={{ color: theme.palette.grey[900] }}>
                          {plan.price}
                        </Typography>
                        {plan.period && (
                          <Typography variant="body1" sx={{ color: theme.palette.grey[600], ml: 0.5 }}>
                            {plan.period}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Divider />
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      {plan.features.map((feature, idx) => (
                        feature && (
                          <Stack
                            key={idx}
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.25 }} />
                            <Typography variant="body2" sx={{ color: theme.palette.grey[700], lineHeight: 1.6 }}>
                              {feature}
                            </Typography>
                          </Stack>
                        )
                      ))}
                    </Stack>
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      onClick={handleScrollToContact}
                      sx={{
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: 2,
                        mt: 2,
                        ...(plan.popular
                          ? {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.common.black,
                              '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                                transform: 'translateY(-2px)',
                              },
                            }
                          : {
                              borderColor: theme.palette.grey[300],
                              color: theme.palette.grey[900],
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                              },
                            }),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Get Started
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Book Demo Section */}
      <Box
        id="demo-section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: theme.palette.common.white,
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={4} alignItems="center">
            <Stack spacing={2} alignItems="center">
              <Chip
                label="Book a Demo"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 32,
                  borderRadius: '20px',
                }}
              />
              <Typography
                variant="h2"
                fontWeight={800}
                textAlign="center"
                sx={{
                  color: theme.palette.grey[900],
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                  letterSpacing: '-0.02em',
                  px: { xs: 2, sm: 0 },
                }}
              >
                See PTS Hub in Action
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                sx={{
                  color: theme.palette.grey[600],
                  maxWidth: { xs: '100%', sm: '600px' },
                  fontWeight: 400,
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  px: { xs: 2, sm: 0 },
                }}
              >
                Schedule a personalized demo and discover how PTS Hub can transform your school management.
              </Typography>
            </Stack>
            <Card
              elevation={0}
              sx={{
                p: 4,
                width: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              }}
            >
              <Box component="form" onSubmit={handleDemoSubmit}>
                <Stack spacing={3}>
                  {demoMessage.text && (
                    <Alert 
                      severity={demoMessage.type === 'success' ? 'success' : 'error'}
                      onClose={() => setDemoMessage({ type: '', text: '' })}
                      sx={{ borderRadius: 2 }}
                    >
                      {demoMessage.text}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        variant="outlined"
                        value={demoForm.name}
                        onChange={handleInputChange('name', 'demo')}
                        required
                        disabled={demoLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        value={demoForm.email}
                        onChange={handleDemoEmailChange}
                        error={!!demoEmailError}
                        helperText={demoEmailError || 'Enter a valid email address'}
                        required
                        disabled={demoLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: demoEmailError 
                                ? alpha(theme.palette.error.main, 0.5)
                                : undefined,
                            },
                            '&:hover fieldset': {
                              borderColor: demoEmailError
                                ? alpha(theme.palette.error.main, 0.7)
                                : undefined,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: demoEmailError
                                ? theme.palette.error.main
                                : undefined,
                            },
                            '&.Mui-error fieldset': {
                              borderColor: alpha(theme.palette.error.main, 0.5),
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: demoEmailError 
                              ? theme.palette.error.main
                              : theme.palette.text.secondary,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        value={demoForm.phone}
                        onChange={handleDemoPhoneChange}
                        error={!!demoPhoneError}
                        helperText={demoPhoneError || 'Enter 10-digit phone number starting with 6, 7, 8, or 9 (required)'}
                        required
                        disabled={demoLoading}
                        inputProps={{
                          maxLength: 10,
                          inputMode: 'numeric',
                          pattern: '[6789][0-9]{9}',
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: demoPhoneError 
                                ? alpha(theme.palette.error.main, 0.5)
                                : undefined,
                            },
                            '&:hover fieldset': {
                              borderColor: demoPhoneError
                                ? alpha(theme.palette.error.main, 0.7)
                                : undefined,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: demoPhoneError
                                ? theme.palette.error.main
                                : undefined,
                            },
                            '&.Mui-error fieldset': {
                              borderColor: alpha(theme.palette.error.main, 0.5),
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: demoPhoneError 
                              ? theme.palette.error.main
                              : theme.palette.text.secondary,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="School Name"
                        variant="outlined"
                        value={demoForm.school}
                        onChange={handleInputChange('school', 'demo')}
                        required
                        disabled={demoLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={demoLoading ? <CircularProgress size={20} color="inherit" /> : <CalendarTodayIcon />}
                    fullWidth
                    disabled={demoLoading}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      borderRadius: 2,
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.common.black,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                      '&:disabled': {
                        bgcolor: theme.palette.primary.main,
                        opacity: 0.7,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {demoLoading ? 'Submitting...' : 'Schedule Demo'}
                  </Button>
                </Stack>
              </Box>
            </Card>
          </Stack>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact-section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" sx={{ mb: 8 }}>
            <Chip
              label="Contact"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 32,
                borderRadius: '20px',
              }}
            />
            <Typography
              variant="h2"
              fontWeight={800}
              textAlign="center"
              sx={{
                color: theme.palette.grey[900],
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                letterSpacing: '-0.02em',
                px: { xs: 2, sm: 0 },
              }}
            >
              Get In Touch
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{
                color: theme.palette.grey[600],
                maxWidth: { xs: '100%', sm: '700px' },
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </Typography>
          </Stack>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                }}
              >
                <Typography variant="h4" fontWeight={800} sx={{ mb: 4, color: theme.palette.grey[900] }}>
                  Contact Information
                </Typography>
                <Stack spacing={3}>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.dark,
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 56,
                        height: 56,
                      }}
                    >
                      <PhoneIcon />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: theme.palette.grey[600], fontWeight: 600, mb: 0.5 }}>
                        Phone
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.grey[900] }}>
                        +91 7411933212
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.dark,
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 56,
                        height: 56,
                      }}
                    >
                      <EmailIcon />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: theme.palette.grey[600], fontWeight: 600, mb: 0.5 }}>
                        Email
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.grey[900] }}>
                        support@ptshub.in
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.dark,
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 56,
                        height: 56,
                      }}
                    >
                      <SupportIcon />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: theme.palette.grey[600], fontWeight: 600, mb: 0.5 }}>
                        Support Hours
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.grey[900] }}>
                        Mon - Fri, 9 AM - 6 PM IST
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.dark,
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 56,
                        height: 56,
                      }}
                    >
                      <InstagramIcon />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: theme.palette.grey[600], fontWeight: 600, mb: 0.5 }}>
                        Instagram
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.grey[900] }}>
                        @ptshub.in
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                }}
              >
                <Typography variant="h4" fontWeight={800} sx={{ mb: 4, color: theme.palette.grey[900] }}>
                  Send us a Message
                </Typography>
                <Box component="form" onSubmit={handleContactSubmit}>
                  <Stack spacing={3}>
                    {contactMessage.text && (
                      <Alert 
                        severity={contactMessage.type === 'success' ? 'success' : 'error'}
                        onClose={() => setContactMessage({ type: '', text: '' })}
                        sx={{ borderRadius: 2 }}
                      >
                        {contactMessage.text}
                      </Alert>
                    )}
                    <TextField
                      fullWidth
                      label="Your Name"
                      variant="outlined"
                      value={contactForm.name}
                      onChange={handleInputChange('name', 'contact')}
                      required
                      disabled={contactLoading}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      value={contactForm.email}
                      onChange={handleContactEmailChange}
                      error={!!contactEmailError}
                      helperText={contactEmailError || 'Enter a valid email address'}
                      required
                      disabled={contactLoading}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: contactEmailError 
                              ? alpha(theme.palette.error.main, 0.5)
                              : undefined,
                          },
                          '&:hover fieldset': {
                            borderColor: contactEmailError
                              ? alpha(theme.palette.error.main, 0.7)
                              : undefined,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: contactEmailError
                              ? theme.palette.error.main
                              : undefined,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.5),
                          },
                        },
                        '& .MuiFormHelperText-root': {
                          color: contactEmailError 
                            ? theme.palette.error.main
                            : theme.palette.text.secondary,
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      value={contactForm.phone}
                      onChange={handleContactPhoneChange}
                      error={!!contactPhoneError}
                      helperText={contactPhoneError || 'Enter 10-digit phone number starting with 6, 7, 8, or 9 (required)'}
                      required
                      disabled={contactLoading}
                      inputProps={{
                        maxLength: 10,
                        inputMode: 'numeric',
                        pattern: '[6789][0-9]{9}',
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: contactPhoneError 
                              ? alpha(theme.palette.error.main, 0.5)
                              : undefined,
                          },
                          '&:hover fieldset': {
                            borderColor: contactPhoneError
                              ? alpha(theme.palette.error.main, 0.7)
                              : undefined,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: contactPhoneError
                              ? theme.palette.error.main
                              : undefined,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.5),
                          },
                        },
                        '& .MuiFormHelperText-root': {
                          color: contactPhoneError 
                            ? theme.palette.error.main
                            : theme.palette.text.secondary,
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={5}
                      variant="outlined"
                      value={contactForm.message}
                      onChange={handleInputChange('message', 'contact')}
                      required
                      disabled={contactLoading}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      endIcon={contactLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      fullWidth
                      disabled={contactLoading}
                      sx={{
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: 2,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.common.black,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        '&:disabled': {
                          bgcolor: theme.palette.primary.main,
                          opacity: 0.7,
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {contactLoading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          bgcolor: theme.palette.grey[900],
          color: theme.palette.common.white,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    component="img"
                    src="/logo1921.png"
                    alt="PTS Hub Logo"
                    sx={{
                      width: 48,
                      height: 48,
                      objectFit: 'contain',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.common.white,
                    }}
                  >
                    PTS Hub
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.7), lineHeight: 1.7 }}>
                  Empowering educational institutions with comprehensive school management solutions.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: theme.palette.common.white }}>
                Quick Links
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  onClick={() => {
                    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: theme.palette.common.white,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Features
                </Button>
                <Button
                  onClick={() => {
                    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: theme.palette.common.white,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Pricing
                </Button>
                <Button
                  onClick={() => {
                    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: theme.palette.common.white,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  onClick={handleScrollToContact}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      color: theme.palette.common.white,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Book Now
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: theme.palette.common.white }}>
                Contact
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                    support@ptshub.in
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhoneIcon sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                    +91 7411933212
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, borderColor: alpha(theme.palette.common.white, 0.1) }} />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.7) }}>
              © {new Date().getFullYear()} PTS Hub. All rights reserved.
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.7) }}>
              Developed by{' '}
              <Box
                component="a"
                href="https://ptshub.in/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                PTS Hub
              </Box>
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductLandingPage;

